import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ClipboardList, Play, CheckCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { PlateChip } from '../components/PlateChip';
import { StatusPill } from '../components/StatusPill';
import { EmptyState } from '../components/EmptyState';
import { PhotoUploader } from '../components/PhotoUploader';
import { api, errMsg } from '../lib/api';
import { FUEL_LABELS } from '../lib/format';

// Technician view — assigned jobs only, NO prices anywhere
export default function TechJobsPage() {
  const [jobs, setJobs] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/tech/jobs');
      setJobs(data.jobs);
    } catch (e) {
      toast.error(errMsg(e));
      setJobs([]);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (jobId, action, successMsg) => {
    setBusyId(jobId);
    try {
      const { data } = await api.post(`/jobs/${jobId}/${action}`);
      toast.success(data.message || successMsg);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  if (jobs === null) {
    return <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold font-display">My jobs</h1>
      {jobs.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No assigned jobs yet." hint="Ask your service advisor to assign you a job." testId="tech-empty-state" />
      ) : (
        jobs.map((job) => (
          <Card key={job.id} data-testid="tech-job-card" className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <PlateChip plate={job.plate} size="lg" />
                  <span className="font-plate text-xs text-[hsl(var(--muted-foreground))]">{job.jo_number}</span>
                </div>
                <StatusPill status={job.status} />
              </div>
              <div className="text-sm font-medium">{job.vehicle}</div>
              <div className="rounded-xl bg-[hsl(var(--muted))] p-3 text-sm">
                <span className="text-[hsl(var(--muted-foreground))]">Complaint: </span>“{job.complaint}”
              </div>
              {(job.odometer_in || job.fuel_level) && (
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  {job.odometer_in ? `Odometer: ${job.odometer_in.toLocaleString()} km` : ''}
                  {job.odometer_in && job.fuel_level ? ' · ' : ''}
                  {job.fuel_level ? `Fuel: ${FUEL_LABELS[job.fuel_level]}` : ''}
                </div>
              )}
              {job.line_items.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-1">Work to do</p>
                  <ul className="space-y-1">
                    {job.line_items.map((it) => (
                      <li key={it.id} className="text-sm flex items-center gap-2">
                        <span className={`text-[10px] uppercase font-medium rounded px-1.5 py-0.5 ${it.type === 'labor' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-800'}`}>{it.type}</span>
                        {it.description} {Number(it.qty) !== 1 ? `×${it.qty}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <p className="text-sm font-medium mb-1">Work photos</p>
                <PhotoUploader jobId={job.id} kind="work" photos={job.photos.filter((p) => p.kind === 'work')} onChanged={load} />
              </div>
              <div className="flex gap-2 pt-1">
                {job.status === 'approved' && (
                  <Button data-testid="tech-start-job-button" disabled={busyId === job.id} onClick={() => act(job.id, 'start', 'Work started')} className="flex-1 h-12 rounded-xl">
                    <Play className="h-4 w-4 mr-1.5" /> Start job
                  </Button>
                )}
                {job.status === 'in_progress' && (
                  <Button data-testid="tech-done-job-button" disabled={busyId === job.id} onClick={() => act(job.id, 'done', 'Marked done')} className="flex-1 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-600/90">
                    <CheckCheck className="h-4 w-4 mr-1.5" /> Mark work done
                  </Button>
                )}
                {job.status === 'ready' && (
                  <p className="text-sm text-[hsl(var(--muted-foreground))] py-2">Done — waiting for release by the advisor.</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
